import { IEnhancedApplePayShippingMethodChangedEvent } from "../models/IEnhancedApplePayShippingMethodChangedEvent";
import { ISelectableApplePayShippingMethod } from "../models/ISelectableApplePayShippingMethod";
import { ApplePayMockModal } from "./ApplePayMockModal";

export class ApplePayMockShippingMethod {
  private modal: ApplePayMockModal;
  private shippingMethods: ISelectableApplePayShippingMethod[] = [];
  private selectedMethod: ISelectableApplePayShippingMethod | null = null;
  private price: string = "";

  private onShippingMethodSelected: (
    event: IEnhancedApplePayShippingMethodChangedEvent,
  ) => void = () => {};
  private shippingMethodComboBox: HTMLSelectElement | null = null;

  constructor(modal: ApplePayMockModal) {
    this.modal = modal;
  }

  public setShippingMethodCallback(
    callback: (event: IEnhancedApplePayShippingMethodChangedEvent) => void,
  ) {
    this.onShippingMethodSelected = callback;
  }

  public initShippingMethodUI(
    shippingMethods: ISelectableApplePayShippingMethod[],
    modalContent: HTMLElement,
    price: string,
  ): void {
    this.shippingMethods = shippingMethods;
    this.price = price;
    const parentShippingMethodContainer = document.createElement("div");
    parentShippingMethodContainer.className = "shippingmethod-parent-container";
    parentShippingMethodContainer.style.marginBottom = "0px";

    const label = document.createElement("label");
    label.textContent = "Method: ";

    this.shippingMethodComboBox = document.createElement("select");
    this.updateTotalAmount();

    // Add shipping methods to the combo-box
    this.shippingMethods.forEach((shippingMethod, index) => {
      const option = document.createElement("option");
      option.value = index.toString();

      option.selected = shippingMethod?.selected ?? false;
      if (shippingMethod?.selected) {
        this.selectedMethod = shippingMethod;
      }

      option.style.fontWeight = "bold";
      option.textContent =
        "£" + shippingMethod.amount + " - " + shippingMethod.label;
      this.shippingMethodComboBox!.appendChild(option);

      const optionDetail = document.createElement("option");
      optionDetail.value = index.toString();
      optionDetail.textContent = shippingMethod.detail;
      optionDetail.disabled = true;
      this.shippingMethodComboBox!.appendChild(optionDetail);
    });

    // Append label and combo-box to the container
    parentShippingMethodContainer.appendChild(label);
    parentShippingMethodContainer.appendChild(this.shippingMethodComboBox);

    // Insert the shipping container after the "Item: Fancy Widget" text
    const shippingContainerPlaceholder = modalContent.querySelector(
      "#shippingmethod-container",
    );
    if (shippingContainerPlaceholder) {
      shippingContainerPlaceholder.appendChild(parentShippingMethodContainer);
    }

    // Add event listener for shipping method selection
    this.shippingMethodComboBox.addEventListener("change", () => {
      this.onShippingMethodChange();
    });
  }

  private onShippingMethodChange(): void {
    const selectedOptionIndex = this.shippingMethodComboBox?.value;
    this.selectedMethod =
      this.shippingMethods[parseInt(selectedOptionIndex || "0", 10)];
    const event = this.wrapEvent(this.selectedMethod);

    // Trigger the callback
    this.onShippingMethodSelected(event);
  }

  private wrapEvent(
    shippingMethodSelected: ISelectableApplePayShippingMethod,
  ): IEnhancedApplePayShippingMethodChangedEvent {
    return {
      shippingMethod: shippingMethodSelected,
      bubbles: false,
      cancelBubble: false,
      cancelable: false,
      composed: false,
      currentTarget: undefined,
      defaultPrevented: false,
      eventPhase: 0,
      isTrusted: false,
      returnValue: false,
      srcElement: undefined,
      target: undefined,
      timeStamp: "",
      type: "",
      composedPath: function (): Node[] {
        throw new Error("Function not implemented.");
      },
      initEvent: function (
        type?: string,
        bubbles?: boolean,
        cancelable?: boolean,
      ): void {
        throw new Error("Function not implemented.");
      },
      preventDefault: function (): void {
        throw new Error("Function not implemented.");
      },
      stopImmediatePropagation: function (): void {
        throw new Error("Function not implemented.");
      },
      stopPropagation: function (): void {
        throw new Error("Function not implemented.");
      },
    };
  }

  public handleShippingMethodUpdate(updateShippingMethodData: any): void {
    const newAmount =
      updateShippingMethodData?.newTotal?.amount ||
      updateShippingMethodData?.status?.newTotal?.amount;

    if (newAmount) {
      this.modal.updateTotalAmount(newAmount);
    }
  }

  private getSelectedShippingMethodOrDefault(): ISelectableApplePayShippingMethod {
    const selectedMethod = this.shippingMethods.find(
      (method) => method.selected,
    );
    return selectedMethod || this.shippingMethods[0];
  }

  private updateTotalAmount() {
    const defaultShipping = this.getSelectedShippingMethodOrDefault();
    if (parseFloat(defaultShipping.amount) > 0) {
      const totalAmount =
        parseFloat(defaultShipping.amount) + parseFloat(this.price);
      this.modal.updateTotalAmount(totalAmount.toString());
    }
  }
}
